import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useDashboard } from '../contexts/DashboardContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Wifi,
  Activity,
  Route,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Flame,
  ShieldAlert,
  CheckCircle2,
  Send,
  Bot,
  Loader2,
  RefreshCw,
  LayoutDashboard,
  Zap,
  Gauge,
  Radio,
  MapPin,
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { deviceDisplayName } from '../utils/deviceName';
import { buildDevicesById, formatRecordDeviceLabel } from '../utils/deviceLabel';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { formatDT, resolveTimezone, formatSpeed, formatDistance } from '../utils/timezone';
import { getTimezoneLabel } from '../utils/timezoneList';
import EventDetailModal from '../components/events/EventDetailModal';

// Device status colors — shared scheme matching Live Tracking & Device Management.
const STATUS_COLORS = {
  moving: '#2563eb',
  online: '#16a34a',
  idle: '#ca8a04',
  stopped: '#dc2626',
  offline: '#6b7280',
};

// Percentage change today vs yesterday. Returns null when there is no baseline
// to compare against meaningfully.
const pctDelta = (today, yesterday) => {
  if (today == null || yesterday == null) return null;
  if (yesterday === 0) return today > 0 ? 100 : 0;
  return Math.round(((today - yesterday) / yesterday) * 100);
};

const WidgetSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3 pt-1 pr-2" data-testid="widget-skeleton">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-2 py-1.5">
        <Skeleton className="w-8 h-8 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2.5 w-1/2" />
        </div>
        <Skeleton className="h-5 w-14 rounded-full shrink-0" />
      </div>
    ))}
  </div>
);

const KPI_PERIODS = {
  today: { label: 'Today', vs: 'vs yesterday', noun: 'today', title: 'Today' },
  week: { label: 'Week', vs: 'vs last week', noun: 'this week', title: 'This Week' },
  month: { label: 'Month', vs: 'vs last month', noun: 'this month', title: 'This Month' },
};

const KpiCard = ({ title, value, icon: Icon, color, subtitle, delta, deltaLabel = 'vs yesterday', testId }) => {
  const tid = testId || title.toLowerCase().replace(/\s/g, '-');
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`kpi-${tid}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1" data-testid={`kpi-${tid}-value`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {delta != null && (
              <div className="flex items-center gap-1 mt-2" data-testid={`kpi-${tid}-delta`}>
                {delta >= 0 ? (
                  <TrendingUp className="w-3 h-3 text-green-500" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-500" />
                )}
                <span className={`text-xs ${delta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {delta > 0 ? '+' : ''}{delta}% {deltaLabel}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MeasuredChart = ({ className, children, testId }) => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setSize({ width: Math.max(0, Math.floor(rect.width)), height: Math.max(0, Math.floor(rect.height)) });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className} data-testid={testId}>
      {size.width > 20 && size.height > 20 ? children(size) : null}
    </div>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { isModuleEnabled } = useFeatureFlags();
  // Persisted UI state (survives navigation + F5)
  const { aiQuery, setAiQuery, aiResponse, setAiResponse } = useDashboard();

  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [topDevices, setTopDevices] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [recentEvents, setRecentEvents] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState('today');
  const [kpiLoading, setKpiLoading] = useState(false);
  const kpiPeriodRef = useRef('today');

  // Check if AI module is enabled for this user
  const aiEnabled = isModuleEnabled('ai_assistant');

  const fetchData = useCallback(async ({ refreshTopDevices = false } = {}) => {
    setLoading(true);
    setTopDevices(null);
    setAlerts(null);
    setRecentEvents(null);
    const topDevicesPath = refreshTopDevices
      ? '/dashboard/top-devices?period_days=7&limit=8&refresh=true'
      : '/dashboard/top-devices?period_days=7&limit=8';
    // Secondary data streams in as it arrives — it must not gate the first paint.
    api.get('/devices/labels').then((r) => setDevices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/event-occurrences?limit=10').then((r) => setRecentEvents(r.data?.items || r.data || [])).catch(() => setRecentEvents([]));
    api.get('/event-occurrences/stats').then((r) => setEventStats(r.data)).catch(() => {});
    api.get(topDevicesPath).then((r) => setTopDevices(Array.isArray(r.data) ? r.data : [])).catch(() => setTopDevices([]));
    api.get('/alerts?status=active&limit=8').then((r) => setAlerts(Array.isArray(r.data) ? r.data : [])).catch(() => setAlerts([]));
    // Only the two cheap KPI calls gate the spinner.
    try {
      const [statsRes, kpisRes] = await Promise.all([
        api.get('/dashboard/stats').catch(() => ({ data: null })),
        api.get(`/dashboard/kpis?period=${kpiPeriodRef.current}`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodChange = async (p) => {
    if (p === kpiPeriod) return;
    setKpiPeriod(p);
    kpiPeriodRef.current = p;
    setKpiLoading(true);
    try {
      const r = await api.get(`/dashboard/kpis?period=${p}`);
      setKpis(r.data);
    } catch (error) {
      toast.error('Failed to load KPIs');
    } finally {
      setKpiLoading(false);
    }
  };

  const handleAiQuery = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    try {
      const response = await api.post('/ai/query', { query: aiQuery });
      setAiResponse(response.data.response);
    } catch (error) {
      console.error('AI query failed:', error);
      toast.error('AI query failed. Please try again.');
      setAiResponse('Sorry, I encountered an error processing your request.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/acknowledge`);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success('Alert acknowledged');
    } catch (error) {
      console.error('Acknowledge failed:', error);
      toast.error('Failed to acknowledge alert');
    }
  };

  // Status donut — mutually-exclusive slices using the shared status scheme.
  const pieData = stats ? [
    { name: 'Moving', value: stats.moving_devices, color: STATUS_COLORS.moving },
    { name: 'Idle', value: stats.idle_devices || 0, color: STATUS_COLORS.idle },
    { name: 'Stopped', value: stats.stopped_devices, color: STATUS_COLORS.stopped },
    { name: 'Offline', value: stats.offline_devices, color: STATUS_COLORS.offline },
  ] : [];

  const onlineNow = kpis?.online_now ?? stats?.online_devices ?? 0;
  const totalDevices = kpis?.total_devices ?? stats?.total_devices ?? 0;
  const onlinePct = totalDevices ? Math.round((onlineNow / totalDevices) * 100) : 0;

  // Live id→device map for re-labelling event/alert rows with the
  // current Asset/Unit. Renames propagate to historical rows without a
  // backend backfill.
  const devicesById = useMemo(() => buildDevicesById(devices), [devices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="hidden lg:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <LayoutDashboard className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground">Welcome back! Here&apos;s your fleet overview.</p>
          </div>
        </div>
        <Button onClick={() => fetchData({ refreshTopDevices: true })} variant="outline" size="sm" className="gap-2" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* KPI Grid — Online now + real current-vs-previous period deltas */}
      <div className="flex items-center justify-end">
        <div className="flex rounded-lg border border-border overflow-hidden" data-testid="kpi-period-switch">
          {Object.entries(KPI_PERIODS).map(([p, meta]) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              data-testid={`kpi-period-${p}`}
              className={`px-3.5 py-1.5 text-xs font-medium transition-colors ${kpiPeriod === p ? 'bg-[#2563eb] text-white' : 'bg-card text-muted-foreground hover:text-foreground'}`}
            >
              {meta.label}
            </button>
          ))}
        </div>
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity ${kpiLoading ? 'opacity-50' : ''}`}>
        <KpiCard
          title="Online Now"
          value={onlineNow}
          icon={Wifi}
          color="bg-green-600"
          subtitle={`of ${totalDevices} devices · ${onlinePct}%`}
        />
        <KpiCard
          title={`Active ${KPI_PERIODS[kpiPeriod].title}`}
          testId="active-today"
          value={kpis?.active_today ?? 0}
          icon={Activity}
          color="bg-[#2563eb]"
          subtitle={`devices reported ${KPI_PERIODS[kpiPeriod].noun}`}
          delta={pctDelta(kpis?.active_today, kpis?.active_yesterday)}
          deltaLabel={KPI_PERIODS[kpiPeriod].vs}
        />
        <KpiCard
          title={`Distance ${KPI_PERIODS[kpiPeriod].title}`}
          testId="distance-today"
          value={formatDistance(kpis?.distance_today_km ?? 0, { decimals: 0 })}
          icon={Route}
          color="bg-cyan-600"
          subtitle={`driven ${KPI_PERIODS[kpiPeriod].noun}`}
          delta={pctDelta(kpis?.distance_today_km, kpis?.distance_yesterday_km)}
          deltaLabel={KPI_PERIODS[kpiPeriod].vs}
        />
        <KpiCard
          title={`Events ${KPI_PERIODS[kpiPeriod].title}`}
          testId="events-today"
          value={kpis?.events_today ?? (kpiPeriod === 'today' ? eventStats?.today : null) ?? 0}
          icon={Zap}
          color="bg-amber-500"
          subtitle={`rule events ${KPI_PERIODS[kpiPeriod].noun}`}
          delta={pctDelta(kpis?.events_today, kpis?.events_yesterday)}
          deltaLabel={KPI_PERIODS[kpiPeriod].vs}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Most Active Devices leaderboard */}
        <Card className="lg:col-span-2" data-testid="top-devices">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Flame className="w-5 h-5 text-amber-500" />
                Most Active Devices
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Most events · last 7 days</p>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {topDevices === null ? (
                <WidgetSkeleton rows={5} />
              ) : topDevices.length === 0 ? (
                <div className="h-56 flex flex-col items-center justify-center text-muted-foreground" data-testid="top-devices-empty">
                  <Flame className="w-10 h-10 mb-3 opacity-50" />
                  <p className="text-sm">No device activity yet</p>
                  <p className="text-xs mt-1">Devices with the most events will rank here</p>
                </div>
              ) : (
                <div className="space-y-2 pr-2">
                  {topDevices.map((d, i) => (
                    <div
                      key={d.device_id}
                      onClick={() => navigate(`/tracking?select=${d.device_id}`)}
                      className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                      data-testid={`top-device-${d.device_id}`}
                    >
                      <div className="w-6 text-center text-sm font-bold text-muted-foreground">{i + 1}</div>
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: STATUS_COLORS[d.status] || STATUS_COLORS.offline }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{deviceDisplayName(d)}</p>
                        <p className="text-xs text-muted-foreground">
                          {d.distance_km > 0 ? `${formatDistance(d.distance_km, { decimals: 0 })} driven` : 'No distance recorded'}
                        </p>
                      </div>
                      <Badge className="bg-[#2563eb] text-white" data-testid={`top-device-events-${d.device_id}`}>
                        {d.events} {d.events === 1 ? 'event' : 'events'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card data-testid="status-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <MeasuredChart className="h-48 w-full min-w-[1px]" testId="status-distribution-chart-canvas">
              {({ width, height }) => (
                <PieChart width={width} height={height}>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              )}
            </MeasuredChart>
            <div className="flex flex-wrap justify-center gap-4 mt-2">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts (unacknowledged, with quick-acknowledge) */}
        <Card data-testid="recent-alerts">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-500" />
                Recent Alerts
              </CardTitle>
              {(alerts?.length ?? 0) > 0 && <Badge className="bg-red-500 text-white">{alerts.length} Active</Badge>}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {alerts === null ? (
                <WidgetSkeleton rows={4} />
              ) : alerts.length === 0 ? (
                <div className="h-56 flex flex-col items-center justify-center text-muted-foreground" data-testid="recent-alerts-empty">
                  <CheckCircle2 className="w-10 h-10 mb-3 text-green-500 opacity-70" />
                  <p className="text-sm">All clear</p>
                  <p className="text-xs mt-1">No active alerts right now</p>
                </div>
              ) : (
                <div className="divide-y">
                  {alerts.map((a) => {
                    const isCritical = a.severity === 'critical';
                    const sevColor = isCritical ? 'text-red-600' : a.severity === 'info' ? 'text-slate-600' : 'text-amber-600';
                    const sevBg = isCritical
                      ? 'bg-red-100 dark:bg-red-900/30'
                      : a.severity === 'info'
                      ? 'bg-slate-100 dark:bg-slate-800'
                      : 'bg-amber-100 dark:bg-amber-900/30';
                    const sevBorder = isCritical ? 'text-red-600 border-red-300' : a.severity === 'info' ? 'text-slate-600 border-slate-300' : 'text-amber-600 border-amber-300';
                    return (
                      <div key={a.id} className="flex items-start gap-3 px-3 py-2.5" data-testid={`alert-${a.id}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${sevBg}`}>
                          <AlertTriangle className={`w-3.5 h-3.5 ${sevColor}`} />
                        </div>
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold">{a.title}</span>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${sevBorder}`}>{a.severity}</Badge>
                          </div>
                          <div className="text-xs font-medium text-slate-700 dark:text-slate-200">
                            {deviceDisplayName({ asset_name: a.asset_name, unit_number: a.unit_number, name: a.device_name })}
                          </div>
                          {a.message && <div className="text-[11px] text-muted-foreground line-clamp-2">{a.message}</div>}
                          <div className="text-[10px] text-muted-foreground">
                            {formatDT(a.created_at, { timezone: resolveTimezone(), seconds: true })}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] gap-1 flex-shrink-0"
                          onClick={() => handleAcknowledge(a.id)}
                          data-testid={`alert-ack-${a.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Ack
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Event Feed Widget */}
        <Card data-testid="event-feed-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#2563eb]" />
                Event Feed
              </CardTitle>
              {eventStats && (
                <div className="flex gap-2">
                  {eventStats.active > 0 && <Badge className="bg-red-500 text-white">{eventStats.active} Active</Badge>}
                  {eventStats.today > 0 && <Badge variant="outline">{eventStats.today} Today</Badge>}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {recentEvents === null ? (
                <WidgetSkeleton rows={4} />
              ) : recentEvents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Zap className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No recent events</p>
                  <p className="text-xs mt-1">Events will appear here when triggered</p>
                </div>
              ) : (
                <div className="divide-y">
                  {recentEvents.map((event) => {
                    const isSpeed = event.event_type?.includes('overspeed');
                    const isGeofenceTransition = ['zone_in', 'zone_out', 'place_in', 'place_out', 'route_entering', 'route_exiting'].includes(event.event_type);
                    const isConnection = ['gprs_connected', 'gprs_not_connected', 'gps_connected', 'gps_not_connected'].includes(event.event_type);
                    const isProximity = ['vehicle_proximity', 'vehicle_proximity_exit'].includes(event.event_type);
                    const isHarshOrCrash = ['harsh_driving', 'crash_detection'].includes(event.event_type);
                    const isVideoEvt = event.event_type === 'video_event';
                    const severityClass = event.severity === 'critical'
                      ? 'bg-red-100 dark:bg-red-900/30'
                      : event.severity === 'warning'
                      ? 'bg-amber-100 dark:bg-amber-900/30'
                      : 'bg-slate-100 dark:bg-slate-800';
                    const iconClass = event.severity === 'critical'
                      ? 'text-red-600'
                      : event.severity === 'warning'
                      ? 'text-amber-600'
                      : 'text-slate-600';
                    const statusBadge = event.status === 'active'
                      ? 'bg-red-100 text-red-700 border-red-200'
                      : event.status === 'cleared'
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : 'bg-slate-100 text-slate-700 border-slate-200';
                    const Icon = isSpeed ? Gauge : isGeofenceTransition ? MapPin : isConnection ? Wifi : isProximity ? Radio : isHarshOrCrash ? AlertTriangle : isVideoEvt ? AlertTriangle : Zap;
                    const eventTz = event.device_timezone || resolveTimezone();

                    return (
                      <div key={event.id} className="flex items-start gap-3 px-3 py-2 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => { setSelectedEvent(event); setEventDetailOpen(true); }} data-testid={`event-feed-${event.id}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${severityClass}`}>
                          <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
                        </div>
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold">{event.event_rule_name}</span>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${event.severity === 'critical' ? 'text-red-600 border-red-300' : event.severity === 'warning' ? 'text-amber-600 border-amber-300' : ''}`}>{event.severity}</Badge>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${statusBadge}`}>{event.status}</Badge>
                          </div>
                          <div className="text-xs font-medium text-slate-700 dark:text-slate-200" data-testid={`event-device-${event.id}`}>{formatRecordDeviceLabel(event, devicesById)}</div>
                          {isSpeed && (
                            <div className="text-[11px] text-red-600 font-medium">
                              {formatSpeed(event.speed_kmh)} (Limit: {formatSpeed(event.threshold_kmh)}){event.road_name ? ` on ${event.road_name}` : ''}
                            </div>
                          )}
                          {!isSpeed && event.message && (
                            <div className="text-[11px] text-muted-foreground">
                              {event.message}
                            </div>
                          )}
                          <div className="text-[10px] text-muted-foreground">
                            {formatDT(event.triggered_at, { timezone: eventTz, seconds: true })} — {getTimezoneLabel(eventTz) || eventTz}
                          </div>
                          {event.duration_secs != null && !(event.metadata?.instant_event || (event.duration_secs === 0 && event.status === 'cleared')) && (
                            <div className="text-[10px] text-muted-foreground">
                              Duration: {event.duration_secs >= 60 ? `${Math.floor(event.duration_secs / 60)}m ${Math.round(event.duration_secs % 60)}s` : `${Math.round(event.duration_secs)}s`}
                            </div>
                          )}
                          {(event.latitude != null && event.longitude != null && (Number(event.latitude) !== 0 || Number(event.longitude) !== 0)) ? (
                            <a
                              href={`https://maps.google.com/?q=${event.latitude},${event.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-[10px] text-blue-500 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              {Number(event.latitude).toFixed(5)}, {Number(event.longitude).toFixed(5)}
                            </a>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* AI Assistant - Only show if AI module is enabled */}
        {aiEnabled && (
          <Card data-testid="ai-assistant">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Bot className="w-5 h-5 text-[#2563eb]" />
                AI Assistant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <form onSubmit={handleAiQuery} className="flex gap-2">
                  <Input
                    placeholder="Ask about your fleet... (e.g., 'How many vehicles are moving?')"
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    className="flex-1"
                    data-testid="ai-input"
                  />
                  <Button type="submit" disabled={aiLoading} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="ai-submit">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>

                {aiResponse && (
                  <div className="p-4 rounded-lg bg-muted/50 border">
                    <p className="text-sm whitespace-pre-wrap">{aiResponse}</p>
                  </div>
                )}

                {!aiResponse && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Ask me anything about your fleet!</p>
                    <p className="text-xs mt-1">Try: &quot;Show me offline vehicles&quot; or &quot;What&apos;s the fleet utilization?&quot;</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        open={eventDetailOpen}
        onOpenChange={setEventDetailOpen}
        devicesById={devicesById}
        onViewDevice={(deviceId, lat, lng, eventId) => navigate(`/tracking?select=${deviceId}${lat != null && lng != null ? `&lat=${lat}&lng=${lng}&zoom=17` : ''}${eventId ? `&event=${eventId}` : ''}`)}
      />
    </div>
  );
};
